import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { MessageSquare, Zap, Shield, Globe, Brain, ArrowRight, CheckCircle, Clock, Users, Star, Sparkles, Bot, Send, Play, Menu, X, ChevronRight, Heart, Flame, Rocket, Sun, Moon } from "lucide-react";
import { chatService } from "../services/chatService";
import Robot3D from "../components/Robot3D";
import "./Welcome.css";

const features = [
  {
    icon: Brain,
    title: "AI-Powered Responses",
    description: "Advanced natural language processing delivers intelligent, context-aware responses that understand your needs.",
    color: "#818cf8"
  },
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Optimized performance ensures instant responses with minimal latency for a seamless experience.",
    color: "#f59e0b"
  },
  {
    icon: Shield,
    title: "Secure & Private",
    description: "Enterprise-grade security with end-to-end encryption protecting your conversations and data.",
    color: "#10b981"
  },
  {
    icon: Globe,
    title: "Multi-Language",
    description: "Communicate in your preferred language with support for 50+ languages and dialects.",
    color: "#ec4899"
  },
  {
    icon: Users,
    title: "Team Collaboration",
    description: "Share conversations, create team workspaces, and collaborate seamlessly with your colleagues.",
    color: "#6366f1"
  },
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "Round-the-clock support ensures you always have assistance whenever you need it.",
    color: "#8b5cf6"
  }
];

const steps = [
  {
    icon: MessageSquare,
    title: "Start a Conversation",
    description: "Simply type your question or request in the chat interface. Our AI understands natural language."
  },
  {
    icon: Brain,
    title: "AI Processes",
    description: "Our advanced AI analyzes your query and generates intelligent, personalized responses."
  },
  {
    icon: CheckCircle,
    title: "Get Results",
    description: "Receive accurate, helpful responses instantly. Continue the conversation for more details."
  }
];

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "Product Manager",
    company: "TechCorp",
    content: "This AI chatbot has revolutionized how we handle customer support. Response times improved by 80%.",
    rating: 5
  },
  {
    name: "Michael Chen",
    role: "CEO",
    company: "StartupXYZ",
    content: "Incredible tool for our team. The AI understands context better than any solution we've tried.",
    rating: 5
  },
  {
    name: "Emily Davis",
    role: "Developer",
    company: "DevStudio",
    content: "The API integration was seamless. Our developers love working with this platform.",
    rating: 5
  }
];

const Welcome = () => {
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [messages, setMessages] = useState([
    { type: "ai", text: "Hello! How can I help you today?" },
    { type: "user", text: "I need help with my project" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ["hero", "features", "how-it-works", "testimonials", "cta"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { type: "user", text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage(userMessage.text);
      setMessages(prev => [...prev, { type: "ai", text: response }]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { type: "ai", text: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className={`welcome-page ${theme}`}>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <Sparkles size={20} />
                <span>Powered by Mohamed Abohappy</span>
              </div>
              <h1>
                <span className="gradient-text">Transform Your</span>
                <br />
                <span className="highlight-text">Conversations</span>
                <br />
                <span>With AI</span>
              </h1>
              <p className="hero-description">
                Experience the future of communication with our intelligent AI assistant. 
                Get instant, accurate responses to any question, anytime.
              </p>
              <div className="hero-buttons">
                <Link to="/chat" className="btn btn-primary btn-large">
                  <span>Start Chatting</span>
                  <ArrowRight size={20} />
                </Link>
                <button className="btn btn-secondary btn-large">
                  <Play size={20} />
                  <span>Watch Demo</span>
                </button>
              </div>

              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">10K+</div>
                  <div className="stat-label">Active Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">1M+</div>
                  <div className="stat-label">Messages/Day</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">99.9%</div>
                  <div className="stat-label">Uptime</div>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="chat-demo">
                <div className="chat-header">
                  <div className="chat-avatar">
                    <Bot size={24} />
                  </div>
                  <div className="chat-info">
                    <div className="chat-name">Mohamed Abohappy</div>
                    <div className="chat-status">Online</div>
                  </div>
                </div>

                <div className="chat-messages">
                  {messages.map((message, index) => (
                    <div key={index} className={`message ${message.type}`}>
                      {message.type === 'ai' && <Bot size={20} />}
                      <div className="message-content">
                        <p>{message.text}</p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="message ai">
                      <Bot size={20} />
                      <div className="message-content">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="chat-input-container">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="chat-input"
                  />
                  <button className="send-button" onClick={handleSendMessage}>
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="container">
          <div className="section-header">
            <h2>
              <Sparkles size={28} />
              Powerful Features
            </h2>
            <p>Everything you need for intelligent conversations</p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" style={{ '--feature-color': feature.color }}>
                <div className="feature-icon" style={{ backgroundColor: feature.color }}>
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="feature-arrow">
                  <ChevronRight size={20} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="steps-section">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="container">
          <div className="section-header">
            <h2>
              <Rocket size={28} />
              Get Started in 3 Steps
            </h2>
            <p>Simple, quick, and easy setup process</p>
          </div>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{index + 1}</div>
                <div className="step-icon">
                  <step.icon size={32} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="cta-section">
        <div className="cta-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
        </div>

        <div className="container">
          <div className="cta-content">
            <Flame size={48} className="cta-icon" />
            <h2>Ready to Transform Your Conversations?</h2>
            <p>Join thousands of users already benefiting from our AI-powered assistant</p>
            <Link to="/chat" className="btn btn-primary btn-large">
              Get Started Free
              <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Bot size={32} />
              <span>Powered by Mohamed Abohappy</span>
            </div>
            <div className="footer-links">
              <a href="#features">Features</a>
              <a href="#how-it-works">How It Works</a>
              <a href="#testimonials">Testimonials</a>
              <Link to="/chat">Get Started</Link>
            </div>
            <div className="footer-social">
              <a href="#" aria-label="Twitter"><Bot size={24} /></a>
              <a href="#" aria-label="GitHub"><Bot size={24} /></a>
              <a href="#" aria-label="Discord"><Bot size={24} /></a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Powered by Mohamed Abohappy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
